/* Budget view */
import { type FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";

import type { ActualsByProject, CostsByPerson, Person, Project } from "@shared/types";
import { downloadCsv, getPerson } from "../data";
import {
  type BudgetBreakdownRow,
  type BudgetMemberBreakdownRow,
  type BudgetSummaryResponse,
  getBudgetBreakdown,
  getBudgetMemberBreakdown,
  getBudgetSummary,
} from "../lib/budget-api";
import { useScrollCurrentIntoView, windowMonths } from "../lib/grid-helpers";
import type { SpaMode } from "../lib/me-api";
import { usePersistedState } from "../lib/viewState";
import { useC } from "../theme";
import { Av, Btn, Card, SBadge, useTableStyles } from "../ui";

export interface BudgetViewProps {
  projects: Project[];
  people: Person[];
  costs: CostsByPerson;
  rangeStart: string;
  rangeEnd: string;
  mode: SpaMode;
  actuals: ActualsByProject;
  role: string;
  currentUserId: number | null;
  openPerson?: (pid: number) => void;
}

export const BudgetView: FC<BudgetViewProps> = ({
  projects,
  people,
  rangeStart,
  rangeEnd,
  mode,
  role,
  currentUserId,
  openPerson,
}) => {
  const { C } = useC();
  const { TH, TD } = useTableStyles();
  const [expanded, setExpanded] = useState<number | null>(null);
  // One-time migration from the pre-convention raw-string keys to the
  // dolmit_<view>_<key> JSON convention. Runs synchronously via a lazy
  // useState initialiser so it lands BEFORE the persisted-state hooks
  // below seed from localStorage. The legacy keys held unquoted values
  // ("all", "1,2,3") that JSON.parse would reject — copy them into the new
  // keys and delete the originals so the JSON helper takes over.
  useState(() => {
    if (typeof localStorage === "undefined") return null;
    const legacyMode = localStorage.getItem("dolmit_budget_filter_mode");
    if (legacyMode !== null) {
      if (localStorage.getItem("dolmit_budget_filterMode") === null) {
        localStorage.setItem("dolmit_budget_filterMode", JSON.stringify(legacyMode));
      }
      localStorage.removeItem("dolmit_budget_filter_mode");
    }
    const legacyIds = localStorage.getItem("dolmit_budget_filter_ids");
    if (legacyIds !== null) {
      if (localStorage.getItem("dolmit_budget_filterIds") === null) {
        const arr = legacyIds.split(",").filter(Boolean).map(Number);
        localStorage.setItem("dolmit_budget_filterIds", JSON.stringify(arr));
      }
      localStorage.removeItem("dolmit_budget_filter_ids");
    }
    return null;
  });
  const [filterMode, setFilterMode] = usePersistedState<string>("budget", "filterMode", "all");
  const [filterIdsArr, setFilterIdsArr] = usePersistedState<number[]>("budget", "filterIds", []);
  const filterIds = useMemo(() => new Set(filterIdsArr), [filterIdsArr]);
  const setFilterIds = useCallback(
    (next: Set<number> | ((prev: Set<number>) => Set<number>)) => {
      setFilterIdsArr((prev) => {
        const resolved = typeof next === "function" ? next(new Set(prev)) : next;
        return [...resolved];
      });
    },
    [setFilterIdsArr],
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  useEffect(() => {
    if (!pickerOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest("[data-budget-filter]")) setPickerOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [pickerOpen]);
  const months = windowMonths(rangeStart, rangeEnd);
  const isActualMode = mode === "actuals";
  /* Visibility rules (product owner 2026-04):
       · Admin        — sees everything (revenue, cost, margin, all projects).
       · Accountant   — sees everything, read-only (same columns as admin).
       · PM           — sees ONLY their own projects, ONLY the revenue column.
                        No cost, no margin, no profit anywhere.
     `isPmView` drives both the project filter and the column/card hiding
     below.  `canSeeCost` == admin || accountant. */
  const canSeeCost = role === "admin" || role === "lead" || role === "accountant";
  const isPmView = role === "pm";
  /* Time-off projects are non-billable — exclude them from Budget entirely.
     They don't contribute revenue, cost or margin; they only model capacity. */
  const baseScope = (isPmView ? projects.filter((p) => p.pmId === currentUserId) : projects).filter(
    (p) => !p.isTimeOff,
  );
  const scopedProjects =
    filterMode === "custom" ? baseScope.filter((p) => filterIds.has(p.id)) : baseScope;
  /* Filter picker shows EVERY project in scope (incl. archived) so users can
     opt in archived ones explicitly. */
  const availableForFilter = baseScope;
  /* When "all" mode, hide archived projects from the table; when the user
     has explicitly picked projects via the filter, honor their selection
     (including any archived ones they opted in). */
  const visibleProjects =
    filterMode === "custom"
      ? scopedProjects
      : scopedProjects.filter((p) => p.status !== "archived");
  useScrollCurrentIntoView(rangeStart, rangeEnd, "budget");

  // Server-driven rollups. Totals card, per-project table, and per-member
  // drill-down come from permission-shaped API endpoints.
  const [summary, setSummary] = useState<BudgetSummaryResponse | null>(null);
  const [breakdown, setBreakdown] = useState<BudgetBreakdownRow[] | null>(null);
  const [memberBreakdown, setMemberBreakdown] = useState<BudgetMemberBreakdownRow[] | null>(null);
  const budgetProjectIds = filterMode === "custom" ? visibleProjects.map((p) => p.id) : undefined;
  const budgetProjectIdsKey = budgetProjectIds?.join(",") ?? "all";
  // biome-ignore lint/correctness/useExhaustiveDependencies: budgetProjectIdsKey drives refetch
  useEffect(() => {
    let cancelled = false;
    getBudgetSummary(budgetProjectIds)
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch(() => {
        if (!cancelled) {
          const empty = { months: [], totals: { revenue: 0, cost: 0, margin: 0 } };
          setSummary({ scoped: empty, total: empty });
        }
      });
    getBudgetBreakdown(budgetProjectIds)
      .then((b) => {
        if (!cancelled) setBreakdown(b);
      })
      .catch(() => {
        if (!cancelled) setBreakdown([]);
      });
    getBudgetMemberBreakdown(budgetProjectIds)
      .then((b) => {
        if (!cancelled) setMemberBreakdown(b);
      })
      .catch(() => {
        if (!cancelled) setMemberBreakdown([]);
      });
    return () => {
      cancelled = true;
    };
  }, [rangeStart, rangeEnd, mode, budgetProjectIdsKey]);

  const projectsById = new Map(projects.map((p) => [p.id, p]));
  const memberBreakdownByProject = new Map((memberBreakdown ?? []).map((p) => [p.projectId, p]));
  const statusRank = (s: string) => (s === "active" ? 1 : s === "archived" ? 99 : 2);
  const breakdownVisible = (breakdown ?? [])
    .filter((b) => (filterMode === "custom" ? filterIds.has(b.projectId) : true))
    // Archived projects with no revenue/cost in the selected period are hidden
    // (their old data isn't relevant to current planning). Active / planned
    // projects always show even when zero. In "custom" mode the user opted
    // them in explicitly — honour that and skip the zero-data filter.
    .filter((b) => {
      if (filterMode === "custom") return true;
      if (b.status !== "archived") return true;
      return b.totals.revenue !== 0 || b.totals.cost !== 0;
    })
    .sort(
      (a, b) =>
        statusRank(a.status) - statusRank(b.status) || a.projectCode.localeCompare(b.projectCode),
    );

  // Per-month cells and the row totals show the "Selected Projects" view —
  // scoped to whatever projectIds the filter narrowed to. The headline KPI
  // cards (Total Revenue / Cost / Margin) read from the full visible-scope
  // block so they keep their portfolio meaning even when a filter is on.
  const ct = months.map((ym) => {
    const s = summary?.scoped.months.find((x) => x.ymKey === ym.key);
    const revenue = s?.revenue ?? 0;
    const cost = s?.cost ?? 0;
    const margin = s?.margin ?? 0;
    return { ...ym, revenue, cost, margin };
  });
  const scopedTotalRev = summary?.scoped.totals.revenue ?? 0;
  const scopedTotalCost = summary?.scoped.totals.cost ?? 0;
  const scopedTotalMargin = summary?.scoped.totals.margin ?? 0;
  const totalRev = summary?.total.totals.revenue ?? 0;
  const totalCost = summary?.total.totals.cost ?? 0;
  const totalMargin = summary?.total.totals.margin ?? 0;
  const totalMarginPct = totalRev > 0 ? (totalMargin / totalRev) * 100 : 0;
  const scopedMarginPct = scopedTotalRev > 0 ? (scopedTotalMargin / scopedTotalRev) * 100 : 0;

  function exportCsv() {
    const rows: (string | number)[][] = [["Row", ...months.map((m) => m.label), "Total"]];
    rows.push([
      "Selected Projects Revenue",
      ...ct.map((x) => Math.round(x.revenue)),
      Math.round(scopedTotalRev),
    ]);
    if (canSeeCost) {
      rows.push([
        "Selected Projects Cost",
        ...ct.map((x) => Math.round(x.cost)),
        Math.round(scopedTotalCost),
      ]);
      rows.push([
        "Selected Projects Margin EUR",
        ...ct.map((x) => Math.round(x.margin)),
        Math.round(scopedTotalMargin),
      ]);
      rows.push([
        "Selected Projects Margin %",
        ...ct.map((x) => (x.revenue > 0 ? Math.round((x.margin / x.revenue) * 100) : 0)),
        Math.round(scopedMarginPct),
      ]);
    }
    for (const b of breakdownVisible) {
      const monthByYm = new Map(b.months.map((m) => [m.ymKey, m]));
      const prows = months.map((ym) => monthByYm.get(ym.key));
      rows.push([
        `${b.projectCode} — ${b.projectName} · Revenue`,
        ...prows.map((r) => r?.revenue ?? 0),
        b.totals.revenue,
      ]);
      if (canSeeCost) {
        rows.push([
          `${b.projectCode} — ${b.projectName} · Cost`,
          ...prows.map((r) => r?.cost ?? 0),
          b.totals.cost,
        ]);
        rows.push([
          `${b.projectCode} — ${b.projectName} · Margin`,
          ...prows.map((r) => r?.margin ?? 0),
          b.totals.margin,
        ]);
      }
    }
    downloadCsv(`dolmit-budget-${mode}.csv`, rows);
  }

  return (
    <div style={{ padding: "22px 26px", maxWidth: 1600, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: C.navyDeep,
              margin: 0,
              fontFamily: "'Poppins',system-ui,sans-serif",
              letterSpacing: -0.3,
            }}
          >
            Budget Overview
          </h2>
          <p style={{ color: C.slateMut, fontSize: 12.5, margin: "3px 0 0" }}>
            {isPmView
              ? isActualMode
                ? "Realised revenue on your projects"
                : "Revenue prognosis on your projects"
              : isActualMode
                ? "Realised revenue & margin from logged hours"
                : "Revenue prognosis & margin tracking"}
            {" · "}
            {filterMode === "custom"
              ? `${visibleProjects.length} of ${availableForFilter.length} projects shown`
              : isPmView
                ? `${visibleProjects.filter((p) => p.status === "active").length} of yours`
                : `${visibleProjects.length} projects`}
            {" · "}
            {months.length} months window
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ position: "relative" }} data-budget-filter>
            <Btn
              small
              onClick={() => setPickerOpen((o) => !o)}
              icon={
                <svg width="11" height="11" viewBox="0 0 14 14" aria-hidden="true">
                  <path
                    d="M1 3h12M3 7h8M5 11h4"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              }
            >
              {filterMode === "all"
                ? `All ${availableForFilter.length} projects`
                : `${filterIds.size} of ${availableForFilter.length} selected`}
            </Btn>
            {pickerOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  right: 0,
                  background: C.surface,
                  border: `1px solid ${C.line}`,
                  borderRadius: 8,
                  boxShadow: C.shL,
                  padding: "10px 4px 10px 10px",
                  minWidth: 300,
                  maxHeight: 360,
                  overflow: "auto",
                  zIndex: 20,
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0 6px 8px",
                    borderBottom: `1px solid ${C.lineSoft}`,
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: C.slateMut,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Filter Projects
                  </span>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      type="button"
                      onClick={() => {
                        setFilterMode("all");
                        setFilterIds(new Set());
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: 10.5,
                        color: C.blue,
                        cursor: "pointer",
                        fontWeight: 600,
                        padding: "2px 4px",
                      }}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFilterMode("custom");
                        setFilterIds(new Set());
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: 10.5,
                        color: C.slateMut,
                        cursor: "pointer",
                        fontWeight: 600,
                        padding: "2px 4px",
                      }}
                    >
                      None
                    </button>
                  </div>
                </div>
                {availableForFilter.map((p) => {
                  const checked = filterMode === "all" || filterIds.has(p.id);
                  return (
                    <label
                      key={p.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "5px 6px",
                        cursor: "pointer",
                        borderRadius: 4,
                        fontSize: 12,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = C.surfaceAlt;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          /* First toggle switches mode from "all" to "custom" with that project excluded. */
                          if (filterMode === "all") {
                            setFilterMode("custom");
                            setFilterIds(
                              new Set(
                                availableForFilter.filter((q) => q.id !== p.id).map((q) => q.id),
                              ),
                            );
                          } else {
                            const n = new Set(filterIds);
                            if (n.has(p.id)) n.delete(p.id);
                            else n.add(p.id);
                            setFilterIds(n);
                          }
                        }}
                        style={{ accentColor: C.blue, cursor: "pointer" }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            color: C.navyDeep,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {p.name}
                        </div>
                        <div style={{ fontSize: 10, color: C.slateMut }}>
                          {p.code} · {p.status}
                        </div>
                      </div>
                    </label>
                  );
                })}
                {availableForFilter.length === 0 && (
                  <div
                    style={{
                      padding: "10px 6px",
                      fontSize: 11.5,
                      color: C.slateMut,
                      fontStyle: "italic",
                    }}
                  >
                    No projects available.
                  </div>
                )}
              </div>
            )}
          </div>
          <Btn
            small
            onClick={exportCsv}
            icon={
              <svg width="11" height="11" viewBox="0 0 14 14" aria-hidden="true">
                <path
                  d="M7 1v8m0 0l-3-3m3 3l3-3M2 12h10"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            }
          >
            Export CSV
          </Btn>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${canSeeCost ? 3 : 1},1fr)`,
          gap: 12,
          marginBottom: 18,
        }}
      >
        <Card pad="14px 18px" style={{ background: C.blueTint2, borderColor: C.blueTint4 }}>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              color: C.slateMut,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 4,
              fontFamily: "'Source Sans Pro',system-ui,sans-serif",
            }}
          >
            Total Revenue
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: C.blue,
              fontVariantNumeric: "tabular-nums",
              fontFamily: "'Poppins',system-ui,sans-serif",
              letterSpacing: -0.5,
            }}
          >
            €{Math.round(totalRev).toLocaleString()}
          </div>
        </Card>
        {canSeeCost && (
          <Card pad="14px 18px" style={{ background: C.canvas, borderColor: C.line }}>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                color: C.slateMut,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                marginBottom: 4,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            >
              Total Cost
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: C.slate,
                fontVariantNumeric: "tabular-nums",
                fontFamily: "'Poppins',system-ui,sans-serif",
                letterSpacing: -0.5,
              }}
            >
              €{Math.round(totalCost).toLocaleString()}
            </div>
          </Card>
        )}
        {canSeeCost && (
          <Card
            pad="14px 18px"
            style={{
              background: totalMarginPct >= 20 ? C.gBg : C.yBg,
              borderColor: totalMarginPct >= 20 ? C.gBd : C.yBd,
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                color: C.slateMut,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                marginBottom: 4,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            >
              Total Margin
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: totalMarginPct >= 20 ? C.g : C.y,
                fontVariantNumeric: "tabular-nums",
                fontFamily: "'Poppins',system-ui,sans-serif",
                letterSpacing: -0.5,
              }}
            >
              €{Math.round(totalMargin).toLocaleString()}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: totalMarginPct >= 20 ? C.g : C.y,
                fontWeight: 600,
                marginTop: 2,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            >
              {Math.round(totalMarginPct)}% margin
            </div>
          </Card>
        )}
      </div>

      <Card style={{ overflow: "auto", maxHeight: "calc(100vh - 280px)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th
                style={{
                  ...TH,
                  textAlign: "left",
                  minWidth: 300,
                  width: 300,
                  position: "sticky",
                  left: 0,
                  top: 0,
                  zIndex: 5,
                  background: C.canvas,
                }}
              >
                Project / Detail
              </th>
              {months.map((m) => {
                const isCurrent = m.isCurrent;
                return (
                  <th
                    key={m.key}
                    data-current-month={isCurrent ? "true" : undefined}
                    style={{
                      ...TH,
                      textAlign: "right",
                      minWidth: 120,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                      background: isCurrent ? C.blueTint2 : C.canvas,
                      borderBottom: `1px solid ${C.line}`,
                      boxShadow: isCurrent ? `inset 0 -2px 0 ${C.blue}` : undefined,
                    }}
                  >
                    {m.label}
                  </th>
                );
              })}
              <th
                style={{
                  ...TH,
                  textAlign: "right",
                  minWidth: 110,
                  borderLeft: `2px solid ${C.blue}`,
                  background: C.blueTint1,
                  position: "sticky",
                  right: 0,
                  top: 0,
                  zIndex: 5,
                  boxShadow: "-4px 0 8px -4px rgba(30,41,71,.12)",
                }}
              >
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Company summary band — tinted background + thicker bottom border
              makes these three rows read as a single KPI block, distinct from
              the per-project rows below. */}
            <tr style={{ background: C.blueTint1 }}>
              <td
                style={{
                  ...TD,
                  textAlign: "left",
                  fontWeight: 700,
                  color: C.blue,
                  fontSize: 13,
                  position: "sticky",
                  left: 0,
                  background: C.blueTint1,
                  zIndex: 2,
                  padding: "8px 10px",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                Selected Projects Revenue
              </td>
              {ct.map((x) => (
                <td
                  key={x.key}
                  style={{
                    ...TD,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 700,
                    color: C.navyDeep,
                    fontSize: 12.5,
                    background: C.blueTint1,
                    padding: "8px 10px",
                  }}
                >
                  {x.revenue > 0 ? `€${Math.round(x.revenue).toLocaleString()}` : "·"}
                </td>
              ))}
              <td
                style={{
                  ...TD,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 800,
                  color: C.navyDeep,
                  background: C.blueTint2,
                  borderLeft: `2px solid ${C.blue}`,
                  position: "sticky",
                  right: 0,
                  zIndex: 2,
                  boxShadow: "-4px 0 8px -4px rgba(30,41,71,.12)",
                  padding: "8px 10px",
                  fontSize: 13,
                }}
              >
                €{Math.round(scopedTotalRev).toLocaleString()}
              </td>
            </tr>
            {canSeeCost && (
              <tr style={{ background: C.blueTint1 }}>
                <td
                  style={{
                    ...TD,
                    textAlign: "left",
                    fontWeight: 700,
                    color: C.slate,
                    fontSize: 13,
                    position: "sticky",
                    left: 0,
                    background: C.blueTint1,
                    zIndex: 2,
                    padding: "8px 10px",
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  Selected Projects Cost
                </td>
                {ct.map((x) => (
                  <td
                    key={x.key}
                    style={{
                      ...TD,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: 600,
                      color: C.slate,
                      fontSize: 12.5,
                      background: C.blueTint1,
                      padding: "8px 10px",
                    }}
                  >
                    {x.cost > 0 ? `€${Math.round(x.cost).toLocaleString()}` : "·"}
                  </td>
                ))}
                <td
                  style={{
                    ...TD,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 800,
                    color: C.slate,
                    background: C.blueTint2,
                    borderLeft: `2px solid ${C.blue}`,
                    position: "sticky",
                    right: 0,
                    zIndex: 2,
                    boxShadow: "-4px 0 8px -4px rgba(30,41,71,.12)",
                    padding: "8px 10px",
                    fontSize: 13,
                  }}
                >
                  €{Math.round(scopedTotalCost).toLocaleString()}
                </td>
              </tr>
            )}
            {canSeeCost && (
              <tr style={{ borderBottom: `3px solid ${C.blue}`, background: C.blueTint1 }}>
                <td
                  style={{
                    ...TD,
                    textAlign: "left",
                    fontWeight: 700,
                    color: scopedMarginPct >= 20 ? C.g : C.y,
                    fontSize: 13,
                    position: "sticky",
                    left: 0,
                    background: C.blueTint1,
                    zIndex: 2,
                    padding: "8px 10px",
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  Selected Projects Margin
                </td>
                {ct.map((x) => {
                  const pct = x.revenue > 0 ? Math.round((x.margin / x.revenue) * 100) : 0;
                  return (
                    <td
                      key={x.key}
                      style={{
                        ...TD,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        fontWeight: 700,
                        color: pct >= 20 ? C.g : pct >= 0 ? C.y : C.r,
                        fontSize: 12.5,
                        background: C.blueTint1,
                        padding: "8px 10px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {x.revenue > 0 ? (
                        <>
                          €{Math.round(x.margin).toLocaleString()}{" "}
                          <span style={{ fontSize: 10.5, fontWeight: 700 }}>({pct}%)</span>
                        </>
                      ) : (
                        "·"
                      )}
                    </td>
                  );
                })}
                <td
                  style={{
                    ...TD,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 800,
                    color: scopedMarginPct >= 20 ? C.g : scopedMarginPct >= 0 ? C.y : C.r,
                    background: C.blueTint2,
                    borderLeft: `2px solid ${C.blue}`,
                    position: "sticky",
                    right: 0,
                    zIndex: 2,
                    boxShadow: "-4px 0 8px -4px rgba(30,41,71,.12)",
                    padding: "8px 10px",
                    fontSize: 13,
                    whiteSpace: "nowrap",
                  }}
                >
                  €{Math.round(scopedTotalMargin).toLocaleString()}{" "}
                  <span style={{ fontSize: 10.5, fontWeight: 800 }}>
                    ({Math.round(scopedMarginPct)}%)
                  </span>
                </td>
              </tr>
            )}
            {breakdownVisible.map((b) => {
              const proj = projectsById.get(b.projectId);
              if (!proj) return null;
              const monthByYm = new Map(b.months.map((m) => [m.ymKey, m]));
              const rows = months.map((ym) => {
                const r = monthByYm.get(ym.key);
                return {
                  revenue: r?.revenue ?? 0,
                  cost: r?.cost ?? 0,
                  margin: r?.margin ?? 0,
                };
              });
              const pTotalRev = b.totals.revenue;
              const pTotalCost = b.totals.cost;
              const pTotalMargin = b.totals.margin;
              const pTotalMarginPct =
                pTotalRev > 0 ? Math.round((pTotalMargin / pTotalRev) * 100) : 0;
              const isExp = expanded === proj.id;
              return (
                <Fragment key={proj.id}>
                  {/* Project header row — always revenue as the primary line.
                  Sub-line shows cost/margin summary for the period (admin/accountant). */}
                  <tr
                    onClick={() => setExpanded(isExp ? null : proj.id)}
                    style={{
                      cursor: "pointer",
                      background: isExp ? C.blueTint1 : "transparent",
                      transition: "background .12s",
                    }}
                    onMouseEnter={(e) => {
                      if (!isExp) e.currentTarget.style.background = C.surfaceAlt;
                    }}
                    onMouseLeave={(e) => {
                      if (!isExp) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <td
                      style={{
                        ...TD,
                        textAlign: "left",
                        position: "sticky",
                        left: 0,
                        background: isExp ? C.blueTint1 : C.surface,
                        zIndex: 2,
                        minWidth: 300,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <span
                          style={{
                            color: C.slateMut,
                            fontSize: 10,
                            transform: isExp ? "rotate(90deg)" : "none",
                            display: "inline-block",
                            transition: "transform .12s",
                            flexShrink: 0,
                          }}
                        >
                          ▶
                        </span>
                        <span
                          style={{
                            fontWeight: 600,
                            color: C.navyDeep,
                            fontSize: 12.5,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            flex: "1 1 auto",
                            minWidth: 0,
                          }}
                          title={proj.name}
                        >
                          {proj.name}
                        </span>
                        <span style={{ flexShrink: 0 }}>
                          <SBadge s={proj.status} />
                        </span>
                      </div>
                      {canSeeCost && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 4,
                            fontSize: 10.5,
                            fontVariantNumeric: "tabular-nums",
                            marginTop: 3,
                            paddingLeft: 18,
                          }}
                        >
                          <span style={{ color: C.slateMut, fontWeight: 500 }}>margin</span>
                          <span
                            style={{
                              color: pTotalMarginPct >= 20 ? C.g : pTotalMarginPct >= 0 ? C.y : C.r,
                              fontWeight: 700,
                            }}
                          >
                            €{Math.round(pTotalMargin).toLocaleString()}
                          </span>
                          <span
                            style={{
                              color: pTotalMarginPct >= 20 ? C.g : pTotalMarginPct >= 0 ? C.y : C.r,
                              fontWeight: 600,
                            }}
                          >
                            ({pTotalMarginPct}%)
                          </span>
                        </div>
                      )}
                    </td>
                    {rows.map((r, i) => (
                      <td
                        key={months[i]?.key ?? i}
                        style={{
                          ...TD,
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                          fontSize: 12,
                          color: C.navy,
                          fontWeight: 500,
                          padding: "6px 8px",
                        }}
                      >
                        {r.revenue > 0 ? (
                          <>
                            <div style={{ fontWeight: 600, color: C.navyDeep }}>
                              {canSeeCost && (
                                <span
                                  style={{
                                    fontSize: 9.5,
                                    color: C.slateMut,
                                    fontWeight: 500,
                                    marginRight: 3,
                                  }}
                                >
                                  rev
                                </span>
                              )}
                              €{Math.round(r.revenue).toLocaleString()}
                            </div>
                            {canSeeCost && r.cost > 0 && (
                              <div style={{ fontSize: 10, color: C.slateMut, marginTop: 1 }}>
                                cost €{Math.round(r.cost).toLocaleString()}
                              </div>
                            )}
                          </>
                        ) : (
                          "·"
                        )}
                      </td>
                    ))}
                    <td
                      style={{
                        ...TD,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        fontWeight: 700,
                        color: C.navyDeep,
                        background: isExp ? C.blueTint2 : C.blueTint1,
                        borderLeft: `2px solid ${C.blue}`,
                        padding: "6px 10px",
                        position: "sticky",
                        right: 0,
                        zIndex: 2,
                        boxShadow: "-4px 0 8px -4px rgba(30,41,71,.12)",
                      }}
                    >
                      <div>
                        {canSeeCost && (
                          <span
                            style={{
                              fontSize: 9.5,
                              color: C.slateMut,
                              fontWeight: 500,
                              marginRight: 3,
                            }}
                          >
                            rev
                          </span>
                        )}
                        €{Math.round(pTotalRev).toLocaleString()}
                      </div>
                      {canSeeCost && (
                        <>
                          <div
                            style={{
                              fontSize: 10,
                              color: C.slateMut,
                              fontWeight: 500,
                              marginTop: 1,
                            }}
                          >
                            cost €{Math.round(pTotalCost).toLocaleString()}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: pTotalMarginPct >= 20 ? C.g : pTotalMarginPct >= 0 ? C.y : C.r,
                              fontWeight: 700,
                              marginTop: 1,
                            }}
                          >
                            margin €{Math.round(pTotalMargin).toLocaleString()}
                          </div>
                        </>
                      )}
                    </td>
                  </tr>

                  {/* Expanded: explicit Cost and Margin rows, then per-member breakdown. */}
                  {isExp && canSeeCost && (
                    <>
                      <tr style={{ background: C.blueTint1 }}>
                        <td
                          style={{
                            ...TD,
                            textAlign: "left",
                            paddingLeft: 32,
                            position: "sticky",
                            left: 0,
                            background: C.blueTint1,
                            zIndex: 2,
                            fontSize: 11.5,
                            color: C.slateMut,
                            fontWeight: 600,
                          }}
                        >
                          Project Cost
                        </td>
                        {rows.map((r, i) => (
                          <td
                            key={months[i]?.key ?? i}
                            style={{
                              ...TD,
                              textAlign: "right",
                              fontVariantNumeric: "tabular-nums",
                              fontSize: 11.5,
                              color: C.slateMut,
                              background: C.blueTint1,
                            }}
                          >
                            {r.cost > 0 ? `€${Math.round(r.cost).toLocaleString()}` : "·"}
                          </td>
                        ))}
                        <td
                          style={{
                            ...TD,
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                            fontWeight: 600,
                            color: C.slate,
                            background: C.blueTint1,
                            borderLeft: `2px solid ${C.blue}`,
                            fontSize: 11.5,
                            position: "sticky",
                            right: 0,
                            zIndex: 2,
                            boxShadow: "-4px 0 8px -4px rgba(30,41,71,.12)",
                          }}
                        >
                          €{Math.round(pTotalCost).toLocaleString()}
                        </td>
                      </tr>
                      <tr style={{ background: C.blueTint1 }}>
                        <td
                          style={{
                            ...TD,
                            textAlign: "left",
                            paddingLeft: 32,
                            position: "sticky",
                            left: 0,
                            background: C.blueTint1,
                            zIndex: 2,
                            fontSize: 11.5,
                            color: pTotalMarginPct >= 20 ? C.g : C.y,
                            fontWeight: 600,
                          }}
                        >
                          Project Margin
                        </td>
                        {rows.map((r, i) => {
                          const pct = r.revenue > 0 ? Math.round((r.margin / r.revenue) * 100) : 0;
                          const mg = r.margin;
                          return (
                            <td
                              key={months[i]?.key ?? i}
                              style={{
                                ...TD,
                                textAlign: "right",
                                fontVariantNumeric: "tabular-nums",
                                fontSize: 11.5,
                                color: pct >= 20 ? C.g : pct >= 0 ? C.y : C.r,
                                fontWeight: 600,
                                background: C.blueTint1,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {r.revenue > 0 ? (
                                <>
                                  €{Math.round(mg).toLocaleString()}{" "}
                                  <span style={{ fontSize: 9.5, fontWeight: 600 }}>({pct}%)</span>
                                </>
                              ) : (
                                "·"
                              )}
                            </td>
                          );
                        })}
                        <td
                          style={{
                            ...TD,
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                            fontWeight: 700,
                            color: pTotalMarginPct >= 20 ? C.g : pTotalMarginPct >= 0 ? C.y : C.r,
                            background: C.blueTint1,
                            borderLeft: `2px solid ${C.blue}`,
                            fontSize: 11.5,
                            position: "sticky",
                            right: 0,
                            zIndex: 2,
                            boxShadow: "-4px 0 8px -4px rgba(30,41,71,.12)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          €{Math.round(pTotalMargin).toLocaleString()}{" "}
                          <span style={{ fontSize: 9.5, fontWeight: 700 }}>
                            ({pTotalMarginPct}%)
                          </span>
                        </td>
                      </tr>
                    </>
                  )}

                  {/* Per-member breakdown. */}
                  {isExp &&
                    (memberBreakdownByProject.get(proj.id)?.members ?? []).map((mem) => {
                      const p = mem.personId ? getPerson(mem.personId, people) : null;
                      const monthByYm = new Map(mem.months.map((m) => [m.ymKey, m]));
                      const mrows = months.map(
                        (ym) =>
                          monthByYm.get(ym.key) ?? {
                            ymKey: ym.key,
                            hours: 0,
                            revenue: 0,
                          },
                      );
                      const mTotalRev = mem.totals.revenue;
                      const mTotalCost = mem.totals.cost ?? 0;
                      return (
                        <tr
                          key={`${proj.id}-m${mem.memberId}`}
                          style={{ background: C.surfaceAlt }}
                        >
                          <td
                            style={{
                              ...TD,
                              textAlign: "left",
                              paddingLeft: 32,
                              position: "sticky",
                              left: 0,
                              background: C.surfaceAlt,
                              zIndex: 2,
                            }}
                          >
                            {p ? (
                              <button
                                type="button"
                                onClick={() => openPerson?.(p.id)}
                                title="Open in Person view"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  cursor: "pointer",
                                  textAlign: "left",
                                }}
                              >
                                <Av name={p.name} sz={20} />
                                <span
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: C.navyDeep,
                                    textDecoration: "underline",
                                    textDecorationColor: "transparent",
                                    textUnderlineOffset: 2,
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.textDecorationColor = C.blue;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.textDecorationColor = "transparent";
                                  }}
                                >
                                  {p.name}
                                </span>
                                <span style={{ fontSize: 10.5, color: C.slateMut }}>
                                  {mem.role}
                                </span>
                              </button>
                            ) : (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  color: C.r,
                                  fontStyle: "italic",
                                  fontSize: 12,
                                }}
                              >
                                <span>? Unassigned</span>
                                <span style={{ fontSize: 10.5 }}>{mem.role}</span>
                              </div>
                            )}
                          </td>
                          {mrows.map((r, i) => (
                            <td
                              key={months[i]?.key ?? i}
                              style={{
                                ...TD,
                                textAlign: "right",
                                fontVariantNumeric: "tabular-nums",
                                fontSize: 11.5,
                                color: C.slate,
                                padding: "5px 8px",
                              }}
                            >
                              {r.revenue > 0 ? (
                                <>
                                  <div>€{Math.round(r.revenue).toLocaleString()}</div>
                                  {canSeeCost && (r.cost ?? 0) > 0 && (
                                    <div style={{ fontSize: 9.5, color: C.slateMut }}>
                                      €{Math.round(r.cost ?? 0).toLocaleString()}
                                    </div>
                                  )}
                                </>
                              ) : (
                                "·"
                              )}
                            </td>
                          ))}
                          <td
                            style={{
                              ...TD,
                              textAlign: "right",
                              fontVariantNumeric: "tabular-nums",
                              fontWeight: 600,
                              color: C.navy,
                              background: C.blueTint1,
                              borderLeft: `2px solid ${C.blue}`,
                              fontSize: 11.5,
                              padding: "5px 10px",
                              position: "sticky",
                              right: 0,
                              zIndex: 2,
                              boxShadow: "-4px 0 8px -4px rgba(30,41,71,.12)",
                            }}
                          >
                            <div>€{Math.round(mTotalRev).toLocaleString()}</div>
                            {canSeeCost && mTotalCost > 0 && (
                              <div style={{ fontSize: 9.5, color: C.slateMut, fontWeight: 500 }}>
                                cost €{Math.round(mTotalCost).toLocaleString()}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
};
